import { useCallback } from 'react';

import type { SetDataOptions } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import type { SingleOrArray } from 'utils/array';
import { toArray } from 'utils/array';
import { type Nullable, type Updater, hasValue, isValue } from 'utils/values';

/** Returns a function that updates the fetched value. */
export const useUpdateByQueryKey = () => {
  const queryClient = useQueryClient();
  return useCallback(
    <T, Input = T>(
      queryKey: SingleOrArray<string>,
      updater: Updater<T, Input>,
      options?: SetDataOptions & { defaultValue: Input }
    ) => {
      const { defaultValue, updatedAt } = options || {};

      // If updater is undefined, remove the cache entry by using removeQueries
      if (isValue(updater) && !hasValue(updater)) {
        return queryClient.removeQueries({ queryKey: toArray(queryKey) });
      }

      const localUpdater = isValue(updater)
        ? updater
        : (cached: Nullable<T>) => {
            const updaterInput = !hasValue(cached) ? defaultValue : (cached as unknown as Input);
            // If cached is undefined, FE has never fetched the data yet.
            // Keep it as is so that the next fetch will take place.
            return !hasValue(updaterInput) ? updaterInput : updater(updaterInput);
          };

      return queryClient.setQueryData<T>(toArray(queryKey), localUpdater, updatedAt ? { updatedAt } : undefined);
    },
    [queryClient]
  );
};

/** Use this helper type if Input is the same as T. */
export type CommonUpdater<T> = Updater<T, T>;
